import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem, Stack, TextField, Skeleton, Box } from '@mui/material';
import axios from 'axios';
import ApiLink from 'API';
import { useNavigate } from 'react-router';
import MainCard from 'ui-component/cards/MainCard';
import LaosMap from './LaosMap';
import DetailPanel from './DetailPanel';
import { fmtKip, makeScale, ACCENT, SCALE_STOPS } from './salesGeoHelpers';
import './salesGeo.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SalesGeoSection = () => {
    const now = useMemo(() => new Date(), []);
    const navigate = useNavigate();
    const [source, setSource] = useState('AONE'); // 'AONE' | 'VIP'
    const [mode, setMode] = useState('year'); // 'month' | 'year' | 'all'
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({ provinces: [], nation: null });
    const [selectedId, setSelectedId] = useState(null);

    const param = useMemo(() => {
        if (mode === 'all') return 'all';
        if (mode === 'month') return `${year}-${String(month).padStart(2, '0')}`;
        return `${year}`;
    }, [mode, year, month]);

    const yearOptions = useMemo(() => {
        const arr = [];
        const cur = now.getFullYear();
        for (let y = cur; y >= cur - 9; y--) arr.push(y);
        return arr;
    }, [now]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                setIsLoading(true);
                const res = await axios.get(ApiLink + '/api/dashboard/sales/geo/' + param + '?source=' + source, {
                    withCredentials: true,
                    headers: { token: localStorage.getItem('token') }
                });
                if (!mounted) return;
                if (res.status === 200) {
                    setData(res.data.data || { provinces: [], nation: null });
                    setIsLoading(false);
                }
            } catch (error) {
                if (!mounted) return;
                setIsLoading(false);
                if (error?.response?.status === 401) {
                    localStorage.clear();
                    navigate(0);
                }
            }
        };
        load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [param, source]);

    const byId = useMemo(() => {
        const m = {};
        (data.provinces || []).forEach((p) => {
            m[p.pr_id] = p;
        });
        return m;
    }, [data]);

    const rampCss = useMemo(() => {
        const s = makeScale(SCALE_STOPS);
        return `linear-gradient(90deg, ${s(0.12)}, ${s(0.4)}, ${s(0.7)}, ${s(1)})`;
    }, []);

    const nation = data.nation;

    return (
        <MainCard
            title="Laos Sales Map · ແຜນທີ່ຍອດຂາຍ"
            secondary={
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <TextField select size="small" label="Source" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 90 }}>
                        <MenuItem value="AONE">AONE</MenuItem>
                        <MenuItem value="VIP">VIP</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Filter" value={mode} onChange={(e) => setMode(e.target.value)} sx={{ minWidth: 90 }}>
                        <MenuItem value="all">All time</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                    </TextField>
                    {mode !== 'all' && (
                        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ minWidth: 90 }}>
                            {yearOptions.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {mode === 'month' && (
                        <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ minWidth: 100 }}>
                            {MONTH_NAMES.map((name, idx) => (
                                <MenuItem key={idx + 1} value={idx + 1}>
                                    {name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Stack>
            }
        >
            <div className="sales-geo">
                {/* KPIs */}
                <div className="geo-topbar">
                    <div className="geo-brand">
                        <h1>ຍອດຂາຍຕາມພູມສາດ</h1>
                        <p>Geographic sales distribution across Laos</p>
                    </div>
                    <div className="kpis">
                        {isLoading || !nation ? (
                            <Skeleton variant="rounded" width={420} height={56} />
                        ) : (
                            <>
                                <div className="kpi">
                                    <span className="kpi-v">{fmtKip(nation.sales)}</span>
                                    <span className="kl-lao">ລວມຍອດຂາຍ</span>
                                    <span className="kl-en">Total sales</span>
                                </div>
                                <div className="kpi">
                                    <span className="kpi-v">{nation.customers}</span>
                                    <span className="kl-lao">ລູກຄ້າ</span>
                                    <span className="kl-en">Customers</span>
                                </div>
                                <div className="kpi">
                                    <span className="kpi-v">{nation.provincesCovered}/18</span>
                                    <span className="kl-lao">ແຂວງທີ່ມີຍອດ</span>
                                    <span className="kl-en">Provinces</span>
                                </div>
                                {nation.top && (
                                    <div className="kpi">
                                        <span className="kpi-v">{nation.top.lao}</span>
                                        <span className="kl-lao">{fmtKip(nation.top.sales)}</span>
                                        <span className="kl-en">Top province</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* stage: map + panel */}
                {isLoading ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Skeleton variant="rounded" sx={{ flex: 1, height: 620 }} />
                        <Skeleton variant="rounded" sx={{ width: 380, height: 620 }} />
                    </Box>
                ) : (
                    <div className="stage">
                        <div className="map-col">
                            <LaosMap
                                provinces={data.provinces}
                                byId={byId}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                                accent={ACCENT}
                                scaleStops={SCALE_STOPS}
                                showLabels
                                showBorders
                            />
                            <div className="legend">
                                <span className="lg-lab">ໜ້ອຍ · Low</span>
                                <div className="lg-ramp" style={{ background: rampCss }} />
                                <span className="lg-lab">ຫຼາຍ · High</span>
                            </div>
                        </div>
                        <DetailPanel provinces={data.provinces} byId={byId} selectedId={selectedId} onSelect={setSelectedId} accent={ACCENT} />
                    </div>
                )}
            </div>
        </MainCard>
    );
};

export default SalesGeoSection;
